'use client'

import { useState, useRef } from 'react'
import { useInView } from '@/hooks/useInView'
import { Plus, Minus } from 'lucide-react'

const FAQS = [
  {
    q: 'What exactly does BuilderLync replace?',
    a: 'BuilderLync replaces your CRM, estimating tool, scheduling software, invoicing system, proposal builder, and follow-up automation. Most contractors ditch 6–8 disconnected apps when they switch.',
  },
  {
    q: 'Is BuilderLync only for roofing contractors?',
    a: 'BuilderLync was built for the way roofing actually works — but it fits any residential contractor: siding, gutters, solar, remodeling, and more. If you run leads, jobs, and crews, it fits.',
  },
  {
    q: 'How long does setup take?',
    a: 'Most contractors are live in 30 days or less when setting up themselves. With our White Glove service, a paid add-on, our team configures everything for you in under 14 days, including data migration, AI agents, proposal templates, automations, and instant estimator setup.',
  },
  {
    q: 'What integrations are included?',
    a: 'QuickBooks, Google Workspace, EagleView aerial measurements, Twilio (SMS & voice), Stripe payments, ABC Supply, SRS Distribution, and QXO. All included in every plan. MCP access available on request for custom integrations.',
  },
  {
    q: 'Does BuilderLync offer a free trial?',
    a: 'No. BuilderLync does not offer a free trial. Book a live demo to see the platform with your own workflows, or watch the recorded demo first. Plans are month-to-month or annual with no contracts, so you can cancel anytime.',
  },
  {
    q: 'What does Sierra AI do?',
    a: 'Sierra is your AI assistant. She qualifies leads, follows up via text and email 24/7, books appointments, answers common customer questions, and never takes a day off. Sierra is included on the Pro plan.',
  },
  {
    q: 'Can I import my existing data?',
    a: 'Yes. Every plan supports CSV imports for contacts, leads, and job history. For hands-on migration by our team, add our White Glove Migration & Setup service, available as a paid add-on.',
  },
  {
    q: "What if I'm locked into a contract with another tool?",
    a: "Most of our competitors do month-to-month. If you're in an annual deal, we'll work with you on timing. Book a demo and we'll map out a clean transition plan.",
  },
  {
    q: 'Does BuilderLync work on phones and tablets?',
    a: 'BuilderLync is a web platform optimized for every device: desktop, tablet and phone. Open it in your phone or tablet browser in the field and you get the same CRM, estimates and job records as in the office, with nothing to install.',
  },
  {
    q: 'What kind of support do I get?',
    a: 'Starter: email support. Pro: priority chat and phone support plus an onboarding session. Enterprise: a dedicated Customer Success Manager and SLA guarantees.',
  },
  {
    q: 'Are there any hidden fees?',
    a: 'No. Your plan price covers the platform and unlimited users. The only extras are pass-through usage costs like EagleView reports and Twilio messages, billed at cost with no markup. Pro includes 10,000 AI credits and 500 AI voice minutes each month, and any additional AI usage is billed as you go.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. There are no contracts. Pay month-to-month or annually, and cancel anytime from your account settings. No cancellation fees, no guilt trips.',
  },
]

/* ── Delay class lookup (max 8) ──────────────────────────── */
const DELAY_CLASSES = [
  'anim-delay-1',
  'anim-delay-2',
  'anim-delay-3',
  'anim-delay-4',
  'anim-delay-5',
  'anim-delay-6',
  'anim-delay-7',
  'anim-delay-8',
]

export function FAQSection() {
  const { ref, isInView } = useInView()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const viewCls = isInView ? 'in-view' : ''

  return (
    <section ref={ref} className="section relative">
      <div className="section-inner">
        {/* Header */}
        <div className={`anim-fade-in-up ${viewCls} text-center mb-12 sm:mb-16`}>
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-3">
            FAQ
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Got Questions?{' '}
            <span className="gradient-text-red">We&apos;ve Got Answers.</span>
          </h2>
        </div>

        {/* FAQ Grid — 2 columns on desktop */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className={`anim-fade-in-up ${DELAY_CLASSES[i % DELAY_CLASSES.length] ?? ''} ${viewCls}`}
            >
              <FAQItem
                question={faq.q}
                answer={faq.a}
                isOpen={openIndex === i}
                onToggle={() =>
                  setOpenIndex(openIndex === i ? null : i)
                }
              />
            </div>
          ))}
        </div>

        {/* FAQ Schema JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: FAQS.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a,
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  )
}

/* ── Single FAQ Item ──────────────────────────────────────── */
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
}) {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <div
      className={`glass-card overflow-hidden transition-all duration-300 ${
        isOpen
          ? 'border-red-600/30 shadow-lg shadow-red-600/5'
          : ''
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-4 sm:p-5 text-left group"
        aria-expanded={isOpen}
      >
        <div
          className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-all duration-300 mt-0.5 ${
            isOpen
              ? 'bg-red-600 text-white'
              : 'bg-zinc-800 text-zinc-400 group-hover:bg-zinc-700'
          }`}
        >
          {isOpen ? <Minus size={14} /> : <Plus size={14} />}
        </div>
        <span
          className={`text-sm sm:text-base font-medium transition-colors duration-200 ${
            isOpen ? 'text-white' : 'text-zinc-300 group-hover:text-white'
          }`}
        >
          {question}
        </span>
      </button>

      {/* Accordion body — CSS grid-rows transition for smooth height */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <div
            ref={contentRef}
            className="px-4 sm:px-5 pb-4 sm:pb-5 pl-[3.25rem]"
            style={{
              opacity: isOpen ? 1 : 0,
              transition: 'opacity 0.2s ease-out 0.05s',
            }}
          >
            <p className="text-sm text-zinc-400 leading-relaxed">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
